import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from './layouts/AppLayout';
import { useAuth } from './auth/AuthProvider';
import type { CrmRole } from './auth/AuthProvider';

// ---- Lazy-loaded page components ----
// Using inline placeholder pages for Phase 1 shell; full implementations come in later plans.

import { MembersPage } from './pages/Members';
import { LoansPage } from './pages/Loans';
import { CampaignsPage } from './pages/Campaigns';
import { CompliancePage } from './pages/Compliance';
import { AlertsPage } from './pages/Alerts';
import { SettingsPage } from './pages/Settings';
import { UnauthorizedPage } from './pages/Unauthorized';
import { LoginPage } from './pages/Login';
import { AuthCallbackPage } from './pages/AuthCallback';
import { DashboardPage } from './pages/Dashboard';
import { StaffReviewQueuePage } from './pages/StaffReviewQueue';
import { TemplatesPage } from './pages/Templates';
import { CrossSellPage } from './pages/CrossSell';
import { CoreLookupPage } from './pages/CoreLookup';
import { CollectionsPage } from './pages/Collections';

// ---- Member 360 Single Pane of Glass (lazy-loaded — large component tree) ----
const Member360Page = lazy(() =>
  import('./pages/member360/Member360Page').then((m) => ({ default: m.Member360Page })),
);

// ---- Loading skeleton used as Suspense fallback ----
function PageSkeleton(): React.ReactElement {
  return (
    <div className="flex flex-col gap-4 p-6 animate-pulse">
      <div className="h-20 bg-sunken rounded-xl" />
      <div className="flex gap-4">
        <div className="w-[30%] h-64 bg-sunken rounded-xl" />
        <div className="flex-1 h-64 bg-sunken rounded-xl" />
      </div>
    </div>
  );
}

// ---- Role-gated route wrapper ----

interface ProtectedRouteProps {
  allowedRoles: CrmRole[];
  children: React.ReactElement;
}

function ProtectedRoute({ allowedRoles, children }: ProtectedRouteProps): React.ReactElement {
  const { isAuthenticated, hasAnyRole } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAnyRole(allowedRoles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

// ---- Auth redirect wrapper ----

/**
 * AuthRoute: redirects authenticated users away from login page.
 */
function AuthRoute({ children }: { children: React.ReactElement }): React.ReactElement {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// ---- Router ----

/**
 * CRM application routing.
 *
 * Route access matrix:
 * | Route          | loan_officer | marketing_staff | compliance_officer | admin |
 * |----------------|:---:|:---:|:---:|:---:|
 * | /members       |  Y  |  Y  |  Y  |  Y  |
 * | /cross-sell    |  Y  |  Y  |  Y  |  Y  |
 * | /review-queue  |  Y  |  Y  |  Y  |  Y  |
 * | /loans         |  Y  |     |  Y  |  Y  |
 * | /collections   |  Y  |     |  Y  |  Y  |
 * | /campaigns     |     |  Y  |     |  Y  |
 * | /compliance    |     |     |  Y  |  Y  |
 * | /alerts        |  Y  |  Y  |  Y  |  Y  |
 * | /settings      |     |     |     |  Y  |
 */
export function AppRouter(): React.ReactElement {
  return (
    <Routes>
      {/* Auth routes (unauthenticated) */}
      <Route
        path="/login"
        element={
          <AuthRoute>
            <LoginPage />
          </AuthRoute>
        }
      />

      {/* Entra ID hand-off: crm-api redirects here with the CRM JWT in the URL fragment */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Protected app routes — wrapped in AppLayout sidebar */}
      <Route
        element={
          <ProtectedRoute allowedRoles={['loan_officer', 'marketing_staff', 'compliance_officer', 'admin']}>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* Default: redirect to /dashboard */}
        <Route index element={<Navigate to="/dashboard" replace />} />

        {/* /dashboard — all authenticated roles */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={['loan_officer', 'marketing_staff', 'compliance_officer', 'admin']}>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        {/* /members — all authenticated roles */}
        <Route
          path="/members"
          element={
            <ProtectedRoute allowedRoles={['loan_officer', 'marketing_staff', 'compliance_officer', 'admin']}>
              <MembersPage />
            </ProtectedRoute>
          }
        />

        {/* /members/:memberId — Member 360 Single Pane of Glass (lazy-loaded) */}
        <Route
          path="/members/:memberId"
          element={
            <ProtectedRoute allowedRoles={['loan_officer', 'marketing_staff', 'compliance_officer', 'admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <Member360Page />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* /core-lookup — live core banking sandbox lookups (Uptiq-brokered) — all authenticated roles */}
        <Route
          path="/core-lookup"
          element={
            <ProtectedRoute allowedRoles={['loan_officer', 'marketing_staff', 'compliance_officer', 'admin']}>
              <CoreLookupPage />
            </ProtectedRoute>
          }
        />

        {/* /cross-sell — all authenticated roles */}
        <Route
          path="/cross-sell"
          element={
            <ProtectedRoute allowedRoles={['loan_officer', 'marketing_staff', 'compliance_officer', 'admin']}>
              <CrossSellPage />
            </ProtectedRoute>
          }
        />

        {/* /review-queue — all authenticated roles */}
        <Route
          path="/review-queue"
          element={
            <ProtectedRoute allowedRoles={['loan_officer', 'marketing_staff', 'compliance_officer', 'admin']}>
              <StaffReviewQueuePage />
            </ProtectedRoute>
          }
        />

        {/* /loans — loan_officer, compliance_officer, admin */}
        <Route
          path="/loans"
          element={
            <ProtectedRoute allowedRoles={['loan_officer', 'compliance_officer', 'admin']}>
              <LoansPage />
            </ProtectedRoute>
          }
        />

        {/* /collections — early-delinquency outreach (Uptiq Collections agent → compliance review) — loan_officer, compliance_officer, admin */}
        <Route
          path="/collections"
          element={
            <ProtectedRoute allowedRoles={['loan_officer', 'compliance_officer', 'admin']}>
              <CollectionsPage />
            </ProtectedRoute>
          }
        />

        {/* /templates — admin, staff (all authenticated) */}
        <Route
          path="/templates"
          element={
            <ProtectedRoute allowedRoles={['loan_officer', 'marketing_staff', 'compliance_officer', 'admin']}>
              <TemplatesPage />
            </ProtectedRoute>
          }
        />

        {/* /campaigns — marketing_staff, admin */}
        <Route
          path="/campaigns"
          element={
            <ProtectedRoute allowedRoles={['marketing_staff', 'admin']}>
              <CampaignsPage />
            </ProtectedRoute>
          }
        />

        {/* /compliance — compliance_officer, admin */}
        <Route
          path="/compliance"
          element={
            <ProtectedRoute allowedRoles={['compliance_officer', 'admin']}>
              <CompliancePage />
            </ProtectedRoute>
          }
        />

        {/* /alerts — all authenticated roles */}
        <Route
          path="/alerts"
          element={
            <ProtectedRoute allowedRoles={['loan_officer', 'marketing_staff', 'compliance_officer', 'admin']}>
              <AlertsPage />
            </ProtectedRoute>
          }
        />

        {/* /settings — admin only */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch-all: redirect to /dashboard (auth guard redirects to login if needed) */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
